import { get_base_url } from '@/shared/http/client';
const DEFAULT_EXPORT_TIMEOUT_MS = 600_000; // 10 minutes for large datasets

function triggerBlobDownload(blob, defaultFilename) {
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = defaultFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
}

async function parseErrorFromBlob(blob) {
    if (!blob || blob.size === 0) {
        return 'Download failed';
    }
    try {
        const text = await blob.text();
        const data = JSON.parse(text);
        return data?.detail || data?.message || text || 'Download failed';
    } catch {
        return 'Download failed';
    }
}

/**
 * Custom hook for exporting data with download functionality
 * @returns {Function} exportData - Function to export data and trigger download
 */
export const useExport = () => {
    /**
     * @param {Object} [exportOptions]
     * @param {(percent: number | null) => void} [exportOptions.onProgress] - null while total size is unknown
     * @param {number} [exportOptions.timeoutMs]
     */
    const exportData = async (
        endpoint,
        requestData,
        defaultFilename = 'export.json',
        onSuccess,
        onError,
        exportOptions = {}
    ) => {
        const { onProgress, timeoutMs = DEFAULT_EXPORT_TIMEOUT_MS } = exportOptions;
        const url = `${get_base_url(origin)}/${endpoint}`;

        await new Promise((resolve) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', url);
            xhr.withCredentials = true;
            xhr.responseType = 'blob';
            xhr.timeout = timeoutMs;
            xhr.setRequestHeader('Content-Type', 'application/json');

            xhr.addEventListener('loadstart', () => {
                onProgress?.(null);
            });

            xhr.addEventListener('progress', (event) => {
                if (event.lengthComputable && event.total > 0) {
                    const pct = Math.min(100, Math.round((event.loaded / event.total) * 100));
                    onProgress?.(pct);
                } else {
                    onProgress?.(null);
                }
            });

            xhr.addEventListener('load', async () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    const blob = xhr.response;
                    if (!(blob instanceof Blob)) {
                        onError?.('Invalid download response');
                        resolve();
                        return;
                    }
                    onProgress?.(100);
                    triggerBlobDownload(blob, defaultFilename);
                    onSuccess?.();
                } else {
                    const detail = await parseErrorFromBlob(xhr.response);
                    onError?.(detail);
                }
                resolve();
            });

            xhr.addEventListener('error', () => {
                onError?.('Network error while downloading');
                resolve();
            });

            xhr.addEventListener('timeout', () => {
                onError?.('Download timed out. Try again or export a smaller dataset.');
                resolve();
            });

            xhr.addEventListener('abort', () => {
                resolve();
            });

            try {
                xhr.send(JSON.stringify(requestData));
            } catch (error) {
                console.error('Error exporting data:', error);
                onError?.(error.message || 'Failed to export data');
                resolve();
            }
        });
    };

    return { exportData };
};
